import React, { useState } from "react";
import ReactDOM from "react-dom";
import {
  Listing,
  ListingPlugin,
  add_highlight,
  clear_highlights,
  linecol_to_pos,
  pos_to_linecol,
} from "nota";
import { rust } from "@codemirror/lang-rust";
import { EditorView } from "@codemirror/view";
import classNames from "classnames";
import axios from "axios";

import "./index.html";

const SAMPLE = `use std::fs::File;
use std::io::prelude::*;

fn main() {
  // This creates f, so it's relevant
  let mut f = File::create("helloworld.txt").unwrap();  

  // write_all takes &mut File as input, so it's a relevant mutation.
  //  the unwrap just checks the error code, so it's not relevant  
  f.write_all(b"Hello world").unwrap();

  // sync_all takes &File, so it's assumed to have no effects on f,
  //   and hence is not considered relevant
  f.sync_all().unwrap();

  // This read of f's metadata doesn't mutate f, so it's not relevant
  let meta = f.metadata().unwrap();

  println!("{:?}", @f@);
  println!("{:?}", meta);
}`;

let App = () => {
  let [editor, set_editor] = useState<EditorView | null>(null);
  let [error, set_error] = useState<string | null>(null);
  let [loading, set_loading] = useState(false);
  let [selected, set_selected] = useState(false); 

  let get_slice = async (range: number[]) => {
    if (!editor) {
      return;
    }

    editor.dispatch({
      effects: clear_highlights.of(null),
    });

    let program = editor.state.doc.toJSON().join("\n");
    let start = pos_to_linecol(editor, range[0]);
    let end = pos_to_linecol(editor, range[1]);

    if (start.line != end.line) {
      throw "Start line different from end line";
    }

    console.log(start, end);

    let request = {
      program,
      line: start.line - 1,
      start: start.col,
      end: end.col,
    };
    let response; 
    try {
      set_loading(true);
      response = await axios.post("https://mindover.computer:3443", request);
      set_loading(false);
    } catch (e) {
      set_loading(false);
      set_error(e.toString());
      return;
    }
    
    console.log(response.data);

    if (response.data.error) {
      set_error(response.data.error);
      return;
    } else {
      set_error(null);
    }

    interface Range {
      start_line: number;
      start_col: number;
      end_line: number;
      end_col: number;
      filename: string;
    }
    let ranges: Range[] = response.data.ranges;

    let highlights = ranges
      .filter((range) => range.filename.includes("main.rs"))
      .filter(
        (range) =>
          !(
            // Exclude sliced variable
            (
              range.start_line == request.line &&
              range.start_col == request.start
            )
          )
      )
      .map((range) => {
        let from = linecol_to_pos(editor!, {
          line: range.start_line + 1,
          col: range.start_col,
        });
        let to = linecol_to_pos(editor!, {
          line: range.end_line + 1,
          col: range.end_col,
        });
        return add_highlight.of({ from, to, color: "peach" });
      });

    highlights.push(
      add_highlight.of({
        from: range[0],
        to: range[1],
        color: "forest-green",
      })
    );

    editor.dispatch({
      effects: highlights,
      // TODO: this doesn't consistently remove the blue selection box
      selection: { anchor: editor.state.selection.main.from },
    });
  };

  let on_click = () => {
    let selection = editor!.state.selection!;
    let range = selection.main;
    if (!range.empty) {
      get_slice([range.from, range.to]);
    } else {
      set_error("You have to select a variable to slice on.");
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h1>Rust Slicer Demo</h1>
        <div>
          <button onClick={on_click} className={classNames({loading, selected})}>{loading ? "..." : "Slice"}</button>
        </div>
      </div>      
      <p>
        This tool demonstrates how{" "}
        <a href="https://github.com/willcrichton/rust-slicer" target="_blank">
          Rust Slicer
        </a>{" "}
        works. Rust Slicer analyzes a Rust program to determine which pieces of
        code influence the value of a given variable.
      </p>
      <p>
        {" "}
        To use the tool, first write a Rust program below. Second, select a
        variable to slice. Finally, click <q>Slice</q>. The{" "}
        <span className="bgcolor-forest-green">green</span> line is the sliced
        variable, and the <span className="bgcolor-peach">peach</span> lines are
        part of the slice. If you the slicer times out, the server may be overloaded &mdash; try again later.
      </p>
      <div style={{ display: "flex", color: "red", justifyContent: "center" }}>
        {error ? <pre>{error}</pre> : null}
      </div>
      <style>{`
      body {
        font-family: Helvetica Neue, sans-serif;
      }

      button {
        font-size: 16px;
        padding: 4px 8px;
        background: #f4f4f4;                
        border: 1px solid black;
        border-radius: 4px;
        color: black;
      }

      button.selected {
        background: rgb(255, 249, 172);
      }

      button.loading {
        background: white;
        border-color: #ccc;
        color: #999;
      }

      h1 {
        margin: 0;
      }

      #app {
        width: 680px;
        margin: 10px auto 0;
      }

      .cm-editor {
        outline: none!important;
        border: 1px solid #ccc;
        border-radius: 4px;
        max-height: 600px;
      }

      .bgcolor-peach { background: rgb(250, 223, 203); }
      .bgcolor-forest-green { background: rgb(186, 220, 199); }
      `}</style>
      <ListingPlugin.Provide>
        <Listing
          editable
          language={rust()}
          code={SAMPLE}
          extensions={[
            EditorView.updateListener.of((update) => {
              if (update.selectionSet) {
                set_selected(!editor.state.selection.main.empty);
              }

              if (update.docChanged) {
                editor!.dispatch({ effects: clear_highlights.of(null) });
              }
            }),
          ]}
          onLoad={(e) => {
            editor = e;
            set_editor(e);
          }}
          delimiters={{
            delimiters: [["@", "@"]],
            onParse: ([range]) => {
              get_slice(range);
            },
          }}
        />
      </ListingPlugin.Provide>
    </div>
  );
};

ReactDOM.render(<App />, document.getElementById("app"));
